import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import store from '@/store'

const trimTrailingSlash = value => value.replace(/\/+$/, '')

const resolveApiBaseUrl = () => {
  const apiBaseFromEnv = String(process.env.VUE_APP_API_BASE_URL || '').trim()
  if (apiBaseFromEnv) {
    return trimTrailingSlash(apiBaseFromEnv)
  }

  const apiUrl = String(process.env.VUE_APP_API_URL || '').trim()
  if (apiUrl) {
    try {
      const parsed = new URL(apiUrl)
      return `${parsed.protocol}//${parsed.host}`
    } catch {
      // Fallback to current origin when VUE_APP_API_URL is malformed.
    }
  }

  return window.location.origin
}

const resolvePusherScheme = () => {
  const configured = String(process.env.VUE_APP_PUSHER_SCHEME || '').trim().toLowerCase()
  if (configured === 'http' || configured === 'https') {
    return configured
  }

  return window.location.protocol === 'https:' ? 'https' : 'http'
}

const resolvePusherCluster = () => {
  const configured = String(process.env.VUE_APP_PUSHER_CLUSTER || '').trim()
  return configured || 'mt1'
}

const resolvePusherHost = (apiBaseUrl, cluster) => {
  const configured = String(process.env.VUE_APP_PUSHER_HOST || '').trim()
  if (configured) {
    if (configured === '0.0.0.0') {
      return window.location.hostname || 'localhost'
    }

    return configured
  }

  if (cluster) {
    return `ws-${cluster}.pusher.com`
  }

  try {
    return new URL(apiBaseUrl).hostname
  } catch {
    return window.location.hostname || 'localhost'
  }
}

const resolvePusherPort = scheme => {
  const configured = Number(process.env.VUE_APP_PUSHER_PORT)
  if (Number.isFinite(configured) && configured > 0) {
    return configured
  }

  return scheme === 'https' ? 443 : 80
}

const initializeEcho = () => {
  if (window.Echo) {
    return window.Echo
  }

  const appKey = String(process.env.VUE_APP_PUSHER_APP_KEY || '').trim()
  if (!appKey) {
    return null
  }

  const apiBaseUrl = resolveApiBaseUrl()
  const scheme = resolvePusherScheme()
  const cluster = resolvePusherCluster()
  const host = resolvePusherHost(apiBaseUrl, cluster)
  const port = resolvePusherPort(scheme)

  window.Pusher = Pusher

  window.Echo = new Echo({
    broadcaster: 'pusher',
    key: appKey,
    cluster,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS: scheme === 'https',
    enabledTransports: ['ws', 'wss'],
    authorizer: channel => ({
      authorize: async (socketId, callback) => {
        const token = store.getters['auth/token']

        if (!token) {
          callback(new Error('Missing auth token for private channel.'), null)
          return
        }

        try {
          const response = await fetch(`${apiBaseUrl}/api/broadcasting/auth`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              socket_id: socketId,
              channel_name: channel.name
            })
          })

          const payload = await response.json().catch(() => ({}))

          if (!response.ok) {
            callback(payload || new Error('Broadcast auth failed.'), null)
            return
          }

          callback(null, payload)
        } catch (error) {
          callback(error, null)
        }
      }
    })
  })

  return window.Echo
}

initializeEcho()

export default window.Echo
export { initializeEcho }
